// « The agent spoke over the team » — per day, target 0 (owner, 2026-10-16).
// An agent message ≤10 min after a person's (panel or phone), with no client
// message in between. ~120 in the 30 days before the fix. Read-only; each recent
// case opens its conversation.
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { whatsappApi } from '@/lib/api';
import { useT } from '@/lib/i18n';

const time = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Casablanca' });

export function AgentOverlapCard() {
  const { t } = useT();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-overlap-daily'],
    queryFn: () => whatsappApi.agentOverlapDaily(),
    staleTime: 60_000,
    refetchInterval: 300_000,
  });
  const days = data?.days ?? [];
  const max = Math.max(1, ...days.map((d) => d.count));
  const today = data?.today ?? 0;
  const ok = today === 0;
  return (
    <div className="mb-7 bg-white rounded-xl border overflow-hidden"
      style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}>
      <div className="px-5 pt-4 pb-3 flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">{t('Agent par-dessus l’équipe')}</p>
          <p className="text-xs text-gray-500 mt-1">{t('Messages de l’agent moins de 10 min après le vôtre, sans nouveau message du client. Objectif : 0.')}</p>
        </div>
        <div className="text-right">
          <p className="text-[28px] font-semibold tabular-nums leading-none"
            style={{ color: isLoading || isError ? 'var(--color-adm-muted)' : ok ? 'var(--color-status-done)' : 'var(--color-status-stuck)' }}>
            {isLoading || isError ? '—' : today}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">{t('aujourd’hui')}</p>
        </div>
      </div>
      {days.length > 0 && (
        <div className="px-5 pb-3">
          <div className="flex items-end gap-1 h-10" aria-label={t('14 derniers jours')}>
            {days.map((d) => (
              <div key={d.day} title={`${d.day} : ${d.count}`} className="flex-1 rounded-sm"
                style={{ height: `${d.count ? Math.max(12, (d.count / max) * 100) : 6}%`, background: d.count ? 'var(--color-status-stuck)' : 'rgba(31, 41, 55, 0.12)' }} />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>{days[0]?.day.slice(5)}</span><span>{t('14 derniers jours')}</span><span>{days[days.length - 1]?.day.slice(5)}</span>
          </div>
        </div>
      )}
      {(data?.recent ?? []).slice(0, 3).map((r) => (
        <Link key={`${r.chatId}:${r.agentAt}`} to={`/admin/assistant?tab=conversations&chat=${encodeURIComponent(r.chatId)}`}
          className="px-5 py-2.5 border-t flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-white/5">
          <span className="text-[11px] tabular-nums text-gray-500 shrink-0">{time(r.agentAt)}</span>
          <span className="text-xs text-gray-700 truncate flex-1">« {r.agentText} »</span>
        </Link>
      ))}
    </div>
  );
}
